import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Close } from 'app/components/Common/Close'
import { Contacts } from 'app/components/Common/Header/Contacts'
import { Props as MenuItemProps } from 'app/components/Common/Header/MenuItem'
import { ModalIBE } from 'app/components/Common/ModalIBE'
import { FlashNews, Props as FlashNewsProps } from 'app/components/FlashNews'
import {
  ChevronRight,
  Email,
  Gift,
  Logo as LogoHeader,
  LogoSticky,
  WhatsappFill,
} from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { HeaderContext } from 'app/containers/Header'
import { theme } from 'app/theme'
import { Media } from 'app/theme/media'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Hamburger } from './Hamburger'
import { Props as LanguageItemProps } from './LanguageItem'
import { Languages } from './Languages'
import { LanguagesMobile } from './LanguagesMobile'
import { Navigation } from './Navigation'
import { Toolbars } from './Toolbars'

export interface Props {
  flashNewsProps?: FlashNewsProps
  benefitsSticker?: boolean
  languagesList: LanguageItemProps[]
  menuItems: MenuItemProps[]
  pageID?: string
  siteName?: string
  variant?: Variant
}

export const Header = memo(function Header({
  flashNewsProps,
  benefitsSticker,
  languagesList,
  menuItems,
  pageID,
}: Props) {
  const {
    email,
    languageCode,
    languagePrefix,
    whatsappURL,
    siteName,
    syncroStatus,
    variant,
  } = useContext(HeaderContext) as any

  const [_locked, setLocked] = useLockedBody()
  const [modalStatus, setModalStatus] = useState(false)
  const [modalIBEStatus, setModalIBEStatus] = useState(false)
  const [startAnimation, setStartAnimation] = useState(false)
  const [scroll, setScroll] = useState(false)

  const [animObjectRef, isAnimObjectVisible] = useInView({ triggerOnce: true })

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 100)

    const onScroll = () => {
      setScroll(window.scrollY > 120)
    }

    window.addEventListener('scroll', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <Container>
      {flashNewsProps ? <FlashNews {...flashNewsProps} /> : null}
      {benefitsSticker ? (
        <BenefitsSticker
          ref={animObjectRef}
          onClick={() => {
            if (!modalStatus) {
              setLocked(true)
              setModalIBEStatus(true)
            } else {
              setLocked(false)
              setModalIBEStatus(false)
            }
          }}
          className={`item-benefits-sticker ${isAnimObjectVisible ? ' visible' : ''}`}
          dial={4}
          row
        >
          <IconWrapper dial={5}>
            <Gift />
          </IconWrapper>
          <Label
            dangerouslySetInnerHTML={{
              __html: `${useVocabularyData('benefits-sticker', languageCode)}`,
            }}
          />
          <ChevronRight />
        </BenefitsSticker>
      ) : null}

      <Head
        className={`${scroll ? 'fixed' : ''}${
          variant === 'compact' ? ' compact' : ''
        }${isAnimObjectVisible ? ' visible' : ''}`}
        dial={4}
        row
        wrap
        space="between"
        ref={animObjectRef}
      >
        <LeftSide dial={4} row>
          <Hamburger
            className={isAnimObjectVisible ? 'visible' : ''}
            isSticky={scroll || variant === 'compact'}
            onClick={() => {
              if (!modalStatus) {
                setLocked(true)
                setModalStatus(true)
              } else {
                setLocked(false)
                setModalStatus(false)
              }

              setTimeout(() => {
                setStartAnimation(true)
              }, 600)
            }}
          />
          <Media greaterThanOrEqual="desktopSmall">
            <Languages
              isSticky={scroll || variant === 'compact'}
              languagesList={languagesList}
              languagePrefix={languagePrefix}
            />
          </Media>
        </LeftSide>

        <Logo
          aria-label={siteName}
          to={languagePrefix ? `/${languagePrefix}` : '/'}
        >
          <Media greaterThanOrEqual="desktopSmall">
            {scroll || variant === 'compact' ? <LogoSticky /> : <LogoHeader />}
          </Media>

          <Media lessThan="desktopSmall">
            <LogoSticky />
          </Media>
        </Logo>

        <Media lessThan="desktopSmall">
          <RightSide dial={6} row>
            <Contacts
              variant="mobile"
              modalStatus={modalStatus}
              startAnimation={startAnimation}
            />
          </RightSide>
        </Media>

        <Media greaterThanOrEqual="desktopSmall">
          <RightSide dial={6} row>
            {email || whatsappURL ? (
              <ContactsCTA dial={5} row space="between">
                {whatsappURL ? (
                  <Anchor
                    aria-label="link"
                    href={whatsappURL}
                    rel="noopener"
                    target="_blank"
                    onClick={() => {
                      typeof window.gtag !== 'undefined' &&
                        window.gtag('event', 'Click', {
                          event_category: 'Website',
                          event_label: 'Whatsapp Number',
                        })
                    }}
                  >
                    <WhatsappFill />
                    WhatsApp
                  </Anchor>
                ) : null}

                {email ? (
                  <Anchor
                    href={`mailto:${email}`}
                    onClick={() => {
                      typeof window.gtag !== 'undefined' &&
                        window.gtag('event', 'Click', {
                          event_category: 'Website',
                          event_label: 'Email Address',
                        })
                    }}
                  >
                    <Email />
                    {useVocabularyData('request', languageCode)}
                  </Anchor>
                ) : null}
              </ContactsCTA>
            ) : null}

            <BookingCTA
              variant="booking-cta"
              label={useVocabularyData('book', languageCode)}
              onClick={() => {
                if (!modalStatus) {
                  setLocked(true)
                  setModalIBEStatus(true)
                } else {
                  setLocked(false)
                  setModalIBEStatus(false)
                }
              }}
            />
          </RightSide>
        </Media>
      </Head>
      {
        <Modal
          className={modalStatus ? 'open' : undefined}
          onClick={() => {
            setLocked(false)
            setModalStatus(false)
            setStartAnimation(false)
          }}
        >
          <Close
            className="close"
            variant="invert"
            onClick={() => {
              setLocked(false)
              setModalStatus(false)
              setStartAnimation(false)
            }}
          />

          <Media lessThan="desktopSmall">
            <LanguagesMobile
              languagesList={languagesList}
              languagePrefix={languagePrefix}
            />
          </Media>

          <Navigation
            menuItems={menuItems}
            modalStatus={modalStatus}
            onClick={() => {
              setLocked(false)
              setModalStatus(false)
            }}
            pageID={pageID}
            siteName={siteName}
          />
        </Modal>
      }

      <Media greaterThanOrEqual="desktopSmall">
        <Modal className={modalIBEStatus ? 'open' : undefined}>
          <Close
            onClick={() => {
              setLocked(false)
              setModalIBEStatus(false)
            }}
          />

          <ModalIBE syncroStatus={syncroStatus} modalStatus={modalIBEStatus} />
        </Modal>
      </Media>

      <Media lessThan="desktopSmall">
        <Toolbars languagePrefix={languagePrefix} />
      </Media>
    </Container>
  )
})

const Container = styled.section`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;

  @media (max-width: 1199px) {
    .item-benefits {
      display: none;
    }
  }
`

const Logo = styled(Link)`
  position: absolute;
  top: 1rem;
  left: 50%;
  opacity: 0;
  transform: translate(-50%, -2.5rem);
  transition: 0.8s 0.4s ease-in-out;

  svg {
    height: 7.5rem;
    width: auto;
    fill: ${theme.colors.variants.neutralLight3};
  }

  @media (max-width: 1199px) {
    opacity: 1;
    display: block;
    margin: 0 auto;
    top: 1.625rem;

    svg {
      height: 3rem;
      width: 5.125rem;
      g {
        fill: ${theme.colors.variants.neutralLight3};
      }
    }
  }
`

const BookingCTA = styled(Button)`
  margin-top: 0.25rem;
  height: 3.875rem;
  padding: 1.125rem 2rem;
  border-radius: 0;
`

const ContactsCTA = styled(FlexBox)``

const Anchor = styled.a`
  color: ${theme.colors.variants.neutralLight3};
  font-family: ${theme.fontFamily.paragraph};
  font-size: 0.75rem;
  line-height: 1rem;
  margin-right: 1.4375rem;

  svg {
    fill: ${theme.colors.variants.neutralLight3};
    margin-right: 0.5rem;
    top: 0.0625rem;
    position: relative;
  }

  &:first-of-type svg {
    width: 0.875rem;
    height: 0.875rem;
  }

  &:last-of-type svg {
    width: 1.125rem;
  }

  &:hover {
    opacity: 0.5;
  }
`

const HeaderFixedStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;

  ${Logo} {
    @media (max-width: 1199px) {
      transition: none;
      svg {
        fill: ${theme.colors.variants.neutralDark2};
      }
    }
  }

  ${ContactsCTA} {
    opacity: 1 !important;
    transition: none !important;
    color: ${theme.colors.variants.neutralDark1};

    svg {
      fill: ${theme.colors.variants.primaryDark};
    }
  }
`

const RightSide = styled(FlexBox)`
  opacity: 0;
  transform: translateY(-2.5rem);
  transition: 0.6s 0.6s ease-in-out;
`

const Head = styled(FlexBox)`
  align-items: start;
  width: 100%;
  transition:
    opacity 1s 0.6s cubic-bezier(0.25, 1, 0.5, 1),
    transform 1s 0.6s cubic-bezier(0.25, 1, 0.5, 1);
  position: relative;
  z-index: 1;
  top: 0;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    height: 12.5rem;
    left: 0;
    right: 0;
    z-index: -2;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark3, 0.6)},
      ${rgba(theme.colors.variants.neutralDark3, 0)}
    );
  }

  @media (max-width: 1199px) {
    &:before {
      height: 6rem;
    }
  }

  &.fixed {
    animation: fadeInTop 0.3s;

    ${HeaderFixedStyle}

    &:before {
      content: none;
    }

    @media (max-width: 1199px) {
      .item-contacts {
        svg {
          fill: ${({ theme }) => theme.colors.variants.neutralDark2};
        }
      }
    }
  }

  .intro-fade-up {
    opacity: 0;
    transform: translateY(-2.5rem);
  }

  &.fixed {
    color: ${({ theme }) => theme.colors.variants.neutralDark1};
    background: ${({ theme }) => theme.colors.variants.neutralLight2};

    ${HeaderFixedStyle}

    ${Logo} {
      top: 1rem;

      svg {
        width: 8.125rem;
        height: 2.25rem;
        top: 0;
        fill: ${({ theme }) => theme.colors.variants.neutralDark2};

        g {
          fill: ${({ theme }) => theme.colors.variants.neutralDark2};
        }
      }
    }

    a {
      color: ${({ theme }) => theme.colors.variants.neutralDark3};
    }
  }

  &.compact {
    animation: none !important;
    color: ${({ theme }) => theme.colors.variants.neutralDark3};
    background: ${({ theme }) => theme.colors.variants.neutralLight2};

    &:before {
      content: none;
    }

    @media (max-width: 1199px) {
      .item-contacts {
        svg {
          fill: ${({ theme }) => theme.colors.variants.neutralDark2};
        }
      }
    }

    ${HeaderFixedStyle}

    ${Logo} {
      top: 1rem;

      svg {
        width: 8.125rem;
        height: 2.25rem;
        top: 0;
        fill: ${({ theme }) => theme.colors.variants.neutralDark2};

        g {
          fill: ${({ theme }) => theme.colors.variants.neutralDark2};
        }
      }
    }

    a {
      color: ${({ theme }) => theme.colors.variants.neutralDark3};
    }
  }

  &.visible {
    opacity: 1;
    transform: none;

    ${Logo} {
      opacity: 1;
      transform: translate(-50%, 0);

      &::before,
      &::after {
        width: 100%;
      }
    }

    .intro-fade-up {
      opacity: 1;
      transform: none;
    }

    ${RightSide} {
      opacity: 1;
      transform: none;
    }
  }

  @keyframes fadeInTop {
    0% {
      top: -4.875rem;
    }
    100% {
      top: 0;
    }
  }
`

const LeftSide = styled(FlexBox)`
  @media (min-width: 1200px) {
    padding-left: 2.125rem;
    padding-top: 1.875rem;
    margin-right: auto;
  }

  @media (max-width: 1199px) {
    padding-left: 1.5rem;
  }
`

const Modal = styled.div`
  width: 100%;
  height: 100%;
  opacity: 0;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: 1;
  transition: opacity 0.2s ease-out;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 1000;

    .close,
    .languages,
    .lang-mobile {
      opacity: 1;
      transform: none;
      transition: 0.3s 1s ease-in-out;
      z-index: 6;
    }
  }

  .close,
  .languages {
    opacity: 0;
    z-index: -1;
  }

  @media (max-width: 1199px) {
    .close {
      background-color: transparent;
      left: 1.5rem;
      top: 1.625rem;
      right: auto;
    }
  }
`

const BenefitsSticker = styled(FlexBox)`
  position: absolute;
  top: calc(100vh - 4rem);
  opacity: 0;
  right: 10vw;
  cursor: pointer;
  transform: translateY(-100%);
  transition: 0.6s 1.2s cubic-bezier(0.25, 1, 0.5, 1);

  &.visible {
    top: calc(100vh - 6rem);
    opacity: 1;
  }

  > svg {
    fill: ${theme.colors.variants.neutralLight3};
    height: 0.625rem;
    margin-left: 0.75rem;
    transition: 0.6s cubic-bezier(0.25, 1, 0.5, 1);
  }

  &:hover {
    > svg {
      transform: translateX(0.375rem);
    }
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const IconWrapper = styled(FlexBox)`
  background: ${theme.colors.variants.primaryDark};
  width: 3rem;
  height: 1.75rem;
  border-radius: 0.25rem;
  margin-right: 0.75rem;

  svg {
    height: 0.75rem;
    width: 1.6875rem;
  }
`

const Label = styled.span`
  color: ${theme.colors.variants.neutralLight3};
  text-transform: uppercase;
  font-family: ${theme.fontFamily.label};
  font-size: 0.75rem;
  line-height: 1rem;
  letter-spacing: 0.1em;
`

export type Variant = 'default' | 'compact'

export type SyncroStatus = 'default' | 'open'
